// screens/mentor/mentordashboard/Header.js
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./../../../assets/css/mentor/mentordashboard/Header.css";

/* ════════════════════════════════════════
   SVG ICONS
   ════════════════════════════════════════ */
const BellIcon = () => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const ChatIcon = () => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const ChevronDownIcon = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    style={{
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.25s ease",
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/* ════════════════════════════════════════
   PAGE META
   ════════════════════════════════════════ */
const PAGE_META = {
  dashboard: {
    title: "Dashboard",
    subtitle: "Welcome back, here's your overview",
  },
  requests: {
    title: "Student Requests",
    subtitle: "Manage mentorship requests",
  },
  assignments: {
    title: "Assignments",
    subtitle: "Track and grade assignments",
  },
  students: { title: "My Students", subtitle: "View enrolled students" },
  mentorprofile: { title: "Mentor Profile", subtitle: "Manage your profile" },
  questions: { title: "Student Questions", subtitle: "Answer student queries" },
  roadmap: { title: "Roadmap", subtitle: "Learning path suggestions" },
  analytics: { title: "Analytics", subtitle: "Performance insights" },
  notifications: { title: "Notifications", subtitle: "" },
  chatlist: { title: "Chats", subtitle: "Your conversations" },
  mycourses: { title: "My Courses", subtitle: "" },
  "help-support": { title: "Help & Support", subtitle: "Get assistance" },
  "payment-received": { title: "Payment Received", subtitle: "Your earnings" },
  "student-report": { title: "Student Report", subtitle: "Detailed reports" },
};

const KEY_PRIORITY = [
  "mentorprofile",
  "mycourses",
  "notifications",
  "assignments",
  "requests",
  "students",
  "questions",
  "chatlist",
  "roadmap",
  "analytics",
  "help-support",
  "payment-received",
  "student-report",
  "dashboard",
];

const getPageKey = (path) => {
  const lower = path.toLowerCase();
  return KEY_PRIORITY.find((key) => lower.includes(key)) || "dashboard";
};

/* ════════════════════════════════════════
   STATIC DATA
   ════════════════════════════════════════ */
const NOTIFICATIONS = [
  {
    id: 1,
    text: "Ali Hassan submitted Assignment #3",
    time: "5 min ago",
    unread: true,
  },
  {
    id: 2,
    text: "New mentorship request from Sara",
    time: "20 min ago",
    unread: true,
  },
  {
    id: 3,
    text: "Weekly session report is ready",
    time: "2 hrs ago",
    unread: false,
  },
];

const CHATS = [
  {
    id: 1,
    name: "Zara Khan",
    msg: "Can we reschedule tomorrow?",
    time: "2m",
    init: "ZK",
    color: "#4f46e5",
  },
  {
    id: 2,
    name: "Omar Farooq",
    msg: "Sent my updated assignment",
    time: "18m",
    init: "OF",
    color: "#059669",
  },
  {
    id: 3,
    name: "Hina Malik",
    msg: "Thank you for the review!",
    time: "1h",
    init: "HM",
    color: "#d97706",
  },
];

/* ════════════════════════════════════════
   HEADER COMPONENT
   ════════════════════════════════════════ */
const MentorHeader = ({ mentorName, mentorAvatar, mentorEmail }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const ref = useRef(null);

  const [meta, setMeta] = useState(PAGE_META.dashboard);
  const [greeting, setGreeting] = useState("");
  const [notifOpen, setNotifOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchVal, setSearchVal] = useState("");

  const avatarLetter = mentorName ? mentorName[0].toUpperCase() : "M";
  const unreadNotif = NOTIFICATIONS.filter((n) => n.unread).length;
  const unreadChat = CHATS.length;

  useEffect(() => {
    const h = new Date().getHours();
    setGreeting(
      h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening",
    );
  }, []);

  useEffect(() => {
    const key = getPageKey(location.pathname);
    setMeta(PAGE_META[key] || PAGE_META.dashboard);
    setNotifOpen(false);
    setChatOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  /* Close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setNotifOpen(false);
        setChatOpen(false);
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (panel) => {
    setNotifOpen(panel === "notif" ? !notifOpen : false);
    setChatOpen(panel === "chat" ? !chatOpen : false);
    setProfileOpen(panel === "profile" ? !profileOpen : false);
  };

  const isDashboard = getPageKey(location.pathname) === "dashboard";

  return (
    <header className="mhdr" ref={ref}>
      {/* ── LEFT ── */}
      <div className="mhdr__left">
        <div className="mhdr__page-row">
          {isDashboard && (
            <p className="mhdr__greeting">
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="#f59e0b"
                className="mhdr__sun"
              >
                <circle cx="12" cy="12" r="5" />
                <line
                  x1="12"
                  y1="1"
                  x2="12"
                  y2="3"
                  stroke="#f59e0b"
                  strokeWidth="2"
                />
                <line
                  x1="12"
                  y1="21"
                  x2="12"
                  y2="23"
                  stroke="#f59e0b"
                  strokeWidth="2"
                />
                <line
                  x1="4.22"
                  y1="4.22"
                  x2="5.64"
                  y2="5.64"
                  stroke="#f59e0b"
                  strokeWidth="2"
                />
                <line
                  x1="18.36"
                  y1="18.36"
                  x2="19.78"
                  y2="19.78"
                  stroke="#f59e0b"
                  strokeWidth="2"
                />
                <line
                  x1="1"
                  y1="12"
                  x2="3"
                  y2="12"
                  stroke="#f59e0b"
                  strokeWidth="2"
                />
                <line
                  x1="21"
                  y1="12"
                  x2="23"
                  y2="12"
                  stroke="#f59e0b"
                  strokeWidth="2"
                />
                <line
                  x1="4.22"
                  y1="19.78"
                  x2="5.64"
                  y2="18.36"
                  stroke="#f59e0b"
                  strokeWidth="2"
                />
                <line
                  x1="18.36"
                  y1="5.64"
                  x2="19.78"
                  y2="4.22"
                  stroke="#f59e0b"
                  strokeWidth="2"
                />
              </svg>
              {/* ── Dynamic greeting with mentor name ── */}
              {greeting}, {mentorName || "Mentor"}!
            </p>
          )}
          <h1 className="mhdr__title">{meta.title}</h1>
          {meta.subtitle && <p className="mhdr__subtitle">{meta.subtitle}</p>}
        </div>
      </div>

      {/* ── SEARCH ── */}
      <div className="mhdr__search-wrap">
        <div className="mhdr__search">
          <span className="mhdr__search-icon">
            <SearchIcon />
          </span>
          <input
            className="mhdr__search-input"
            placeholder="Search students, assignments..."
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
          />
        </div>
      </div>

      {/* ── RIGHT ── */}
      <div className="mhdr__right">
        <div className="mhdr__divider" />

        {/* Notifications */}
        <div className="mhdr__btn-wrap" onClick={(e) => e.stopPropagation()}>
          <button
            className={`mhdr__icon-btn${notifOpen ? " active" : ""}`}
            onClick={() => toggle("notif")}
            title="Notifications"
          >
            <BellIcon />
            {unreadNotif > 0 && (
              <span className="mhdr__badge">{unreadNotif}</span>
            )}
          </button>

          {notifOpen && (
            <div className="mhdr__dropdown">
              <div className="mhdr__dropdown-head">
                <span className="mhdr__dropdown-title">Notifications</span>
                <span className="mhdr__dropdown-count">{unreadNotif} new</span>
              </div>
              <div className="mhdr__dropdown-list">
                {NOTIFICATIONS.map((n) => (
                  <div
                    key={n.id}
                    className={`mhdr__notif-row${n.unread ? " unread" : ""}`}
                  >
                    <span className={`mhdr__dot${n.unread ? "" : " read"}`} />
                    <div>
                      <p className="mhdr__notif-text">{n.text}</p>
                      <span className="mhdr__notif-time">{n.time}</span>
                    </div>
                  </div>
                ))}
              </div>
              <button
                className="mhdr__dropdown-footer"
                onClick={() => {
                  navigate("/mentordashboard/notifications");
                  setNotifOpen(false);
                }}
              >
                View all notifications <ArrowRightIcon />
              </button>
            </div>
          )}
        </div>

        {/* Chat */}
        <div className="mhdr__btn-wrap" onClick={(e) => e.stopPropagation()}>
          <button
            className={`mhdr__icon-btn${chatOpen ? " active" : ""}`}
            onClick={() => toggle("chat")}
            title="Messages"
          >
            <ChatIcon />
            {unreadChat > 0 && (
              <span className="mhdr__badge">{unreadChat}</span>
            )}
          </button>

          {chatOpen && (
            <div className="mhdr__dropdown">
              <div className="mhdr__dropdown-head">
                <span className="mhdr__dropdown-title">Messages</span>
                <span className="mhdr__dropdown-count">
                  {unreadChat} unread
                </span>
              </div>
              <div className="mhdr__dropdown-list">
                {CHATS.map((c) => (
                  <div key={c.id} className="mhdr__chat-row">
                    <div
                      className="mhdr__chat-avi"
                      style={{ background: c.color }}
                    >
                      {c.init}
                    </div>
                    <div className="mhdr__chat-info">
                      <span className="mhdr__chat-name">{c.name}</span>
                      <span className="mhdr__chat-msg">{c.msg}</span>
                    </div>
                    <span className="mhdr__chat-time">{c.time}</span>
                  </div>
                ))}
              </div>
              <button
                className="mhdr__dropdown-footer"
                onClick={() => {
                  navigate("/mentordashboard/chatlist");
                  setChatOpen(false);
                }}
              >
                Open all chats <ArrowRightIcon />
              </button>
            </div>
          )}
        </div>

        <div className="mhdr__divider" />

        {/* Avatar / Profile */}
        <div className="mhdr__btn-wrap" onClick={(e) => e.stopPropagation()}>
          <button
            className={`mhdr__profile-btn${profileOpen ? " active" : ""}`}
            onClick={() => toggle("profile")}
          >
            <div className="mhdr__avatar">
              {mentorAvatar ? (
                <img
                  src={mentorAvatar}
                  alt="avatar"
                  className="mhdr__avatar-img"
                />
              ) : (
                <span className="mhdr__avatar-letter">{avatarLetter}</span>
              )}
              <span className="mhdr__avatar-status" />
            </div>
            <ChevronDownIcon open={profileOpen} />
          </button>

          {profileOpen && (
            <div className="mhdr__dropdown mhdr__profile-panel">
              <div className="mhdr__profile-banner">
                <div className="mhdr__banner-avi">
                  {mentorAvatar ? (
                    <img
                      src={mentorAvatar}
                      alt="avatar"
                      className="mhdr__avatar-img"
                    />
                  ) : (
                    <span>{avatarLetter}</span>
                  )}
                </div>
                <div>
                  {/* ── Dynamic name & email ── */}
                  <p className="mhdr__banner-name">{mentorName || "Mentor"}</p>
                  <p className="mhdr__banner-email">
                    {mentorEmail || "mentor@example.com"}
                  </p>
                  <span className="mhdr__active-tag">
                    <span className="mhdr__tag-dot" /> Active
                  </span>
                </div>
              </div>
              <div className="mhdr__menu">
                {[
                  [
                    "👤",
                    "Manage Your Profile",
                    "/mentor/dashboard/mentorprofile",
                  ],
                ].map(([icon, label, path]) => (
                  <button
                    key={label}
                    className="mhdr__menu-row"
                    onClick={() => {
                      if (path) {
                        navigate(path);
                        setProfileOpen(false);
                      }
                    }}
                  >
                    <span className="mhdr__menu-ico">{icon}</span>
                    <span className="mhdr__menu-lbl">{label}</span>
                  </button>
                ))}
                <div className="mhdr__menu-sep" />
                <button
                  className="mhdr__menu-row mhdr__logout-row"
                  onClick={() => navigate("/mentor-auth")}
                >
                  <span className="mhdr__menu-ico">🚪</span>
                  <span className="mhdr__menu-lbl">Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default MentorHeader;
